import { HttpErrorResponse } from '@angular/common/http';
import { Injector } from '@angular/core';
import { Router } from '@angular/router';
import { BeneficiariesSuccessCodes, BeneficiaryErrorCodes } from '@app/translations';
import { FormActionTypeEnum } from '@finxone-platform/form-action';
import { AlertHandlerService } from '@finxone-platform/shared/services';
import {
  ACCOUNTTYPE,
  APP_ZONES,
  BaseWidgetProperties,
  ButtonActionType,
  ConfirmationClaimType,
} from '@finxone-platform/shared/sys-config-types';
import { PaymentRequestPayload } from '@finxone-platform/shared/utils';
import { Store } from '@ngxs/store';
import { catchError, map, take } from 'rxjs';
import { UpdateBeneficiary } from '../../actions/beneficiary.action';
import { GeneratePaymentRequest } from '../../actions/payment-request.action';
import { AccountService } from '../../services/account-service/account-service.service';
import { AddBeneficiaryRequest, UpdateBeneficiaryRequest } from '../../services/account-service/account.type';
import { BackOverrideService } from '../../services/back-override-service/back-override.service';
import { ConfirmationService } from '../../services/confirmation-service/confirmation-service';
import { ApiActions } from '../../state/api-loading.state';
import { FormActionState } from '../../state/form-submision.state';
import { PaymentRequestState } from '../../state/payment-request.state';
import { ProjectSettingsState } from '../../state/project-settings.state';
import { findAndShowAlertFromCode } from '../alert-code-utils/alert-code.utils';
import { removeBeneficiaryFromAccount } from '../cta-button-actions/beneficiary-zone/remove-beneficiary.utils';
import { submitEditBeneficiaryRequest } from '../cta-button-actions/beneficiary-zone/submit-edit-beneficiary-request.utils';
import { mapFormActionDataToAddBeneficiaryRequest } from '../cta-button-actions/domestic-payments/add-beneficiary';
import { formatNameAsUrl } from '../zone-url.utils';

export function updateBeneficiary(injector: Injector) {
  const store = injector.get(Store);
  const confirmationService = injector.get(ConfirmationService);

  const formData = store.selectSnapshot(FormActionState.getFormActionState);
  const payload = formData?.[FormActionTypeEnum.EDIT_BENEFICIARY]?.formData
    ?.payload as UpdateBeneficiaryRequest;
  const bid = formData?.[FormActionTypeEnum.EDIT_BENEFICIARY]?.formData?.payload?.id;
  const fromData = mapFormActionDataToAddBeneficiaryRequest(payload);

  const beneficiaryConfirmationEnabled = store.selectSnapshot(ProjectSettingsState.getProjectSettings)
    .authentication?.inApp?.beneficiaryConfirmationEnabled;

  if (beneficiaryConfirmationEnabled) {
    const loadingAction = ApiActions.addEditDomesticBeneficiary;
    const subscription = confirmationService.checkConfirmation(undefined, loadingAction).subscribe(() => {
      store.dispatch(new UpdateBeneficiary(fromData, bid));
      subscription.unsubscribe();
    });
    confirmationService.confirmAction(ConfirmationClaimType.UPDATE_BENEFICIARY, undefined, true);
  } else {
    store.dispatch(new UpdateBeneficiary(fromData, bid));
  }
}

export function updateEditIntlBeneficiary(injector: Injector) {
  const widgetProperties: BaseWidgetProperties = {
    urlToNavigate: 'beneficiary-list',
    zoneToNavigate: APP_ZONES.BENEFICIARY_ZONE,
    textContent: '',
  };
  submitEditBeneficiaryRequest(widgetProperties, injector);
}

// TODO: duplicated function? delete before merge
export function addBeneficiary(injector: Injector) {
  const accountService = injector.get(AccountService);
  const router = injector.get(Router);
  const store = injector.get(Store);
  const alertHandlerService = injector.get(AlertHandlerService);
  const formData = store.selectSnapshot(FormActionState.getFormActionState);
  const payload = formData?.[FormActionTypeEnum.ADD_BENEFICIARY]?.formData as AddBeneficiaryRequest;
  const backService = injector.get(BackOverrideService);
  const formValues = mapFormActionDataToAddBeneficiaryRequest(payload);
  accountService
    .addBeneficiary(formValues)
    .pipe(
      take(1),
      map(() => {
        const nextRoute = `/zones/${formatNameAsUrl(APP_ZONES.PAYMENT)}/select-beneficiary`;
        router.navigateByUrl(nextRoute);
        backService.setBackOverride('/zones/landing/home', nextRoute, FormActionTypeEnum.ADD_BENEFICIARY);
        findAndShowAlertFromCode(store, router, alertHandlerService, [
          BeneficiariesSuccessCodes.ADD_BENEFICIARY,
        ]);
      }),
      catchError(() => {
        const endRoute =
          payload.accountType === ACCOUNTTYPE.PERSONAL
            ? `add-beneficiary#personal`
            : `add-beneficiary#business`;

        findAndShowAlertFromCode(store, router, alertHandlerService, [
          BeneficiaryErrorCodes.FAILED_TO_CREATE_BENEFICIARY,
        ]);
        return router.navigateByUrl(`/zones/${formatNameAsUrl(APP_ZONES.PAYMENT)}/${endRoute}`);
      }),
    )
    .subscribe();
}

export function removeBeneficiary(injector: Injector) {
  const widgetProperties: BaseWidgetProperties = {
    buttonActionType: ButtonActionType.REMOVE_BENEFICIARY,
    zoneToNavigate: APP_ZONES.BENEFICIARY_ZONE,
    urlToNavigate: 'beneficiary-list',
    textContent: '',
  };
  removeBeneficiaryFromAccount(widgetProperties, injector);
}

export function paymentRequest(injector: Injector) {
  const _router = injector.get(Router);
  const alertHandlerService = injector.get(AlertHandlerService);
  const store = injector.get(Store);
  const data = store.selectSnapshot(PaymentRequestState.paymentRequest).paymentRequestPayload;
  const payload: PaymentRequestPayload = {
    ids: data?.ids ?? [],
    approvedStatus: data?.approvedStatus ?? '',
  };
  store.dispatch(new GeneratePaymentRequest(payload)).subscribe({
    next: () => {
      alertHandlerService.showAlertFn('success', `Payment ${payload.approvedStatus} successfully.`);
      _router.navigateByUrl(
        `/zones/${formatNameAsUrl(APP_ZONES.PAYMENT)}/pending-and-approved-payment-requests`,
      );
    },
    error: (err: HttpErrorResponse) => {
      if (err.status !== 404) {
        _router.navigateByUrl(
          `/zones/${formatNameAsUrl(APP_ZONES.PAYMENT)}/pending-and-approved-payment-requests`,
        );
      }
      console.error('Error generating payment request:', err);
    },
  });
}
